import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, DollarSign, Mountain, Calendar, ChevronRight, MapPin, Check, Compass } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Layout from "@/components/layout/Layout";
import PageHero from "@/components/shared/PageHero";
import { AdBanner } from "@/components/shared/AdPlaceholder";
import axios from "axios";
import { API } from "@/App";
import { getImageUrl } from "@/utils/imageUrl";

const difficultyColors = {
  "Fácil": "bg-green-100 text-green-800",
  "Moderado": "bg-yellow-100 text-yellow-800",
  "Difícil": "bg-red-100 text-red-800",
};

function GuideCard({ guide }) {
  const placeholderImage = "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="group"
    >
      <Link
        to={`/guias/${guide.slug}`}
        className="block bg-white rounded-3xl overflow-hidden shadow-card hover:shadow-card-hover transition-all duration-500"
        data-testid={`guide-card-${guide.slug}`}
      >
        {/* Image */}
        <div className="relative aspect-[16/9] overflow-hidden">
          <img
            src={getImageUrl(guide.featured_image_url) || placeholderImage}
            alt={guide.title}
            className="w-full h-full object-cover img-zoom"
          />
          {guide.difficulty && (
            <Badge className={`absolute top-4 left-4 ${difficultyColors[guide.difficulty] || "bg-gray-100"}`}>
              {guide.difficulty}
            </Badge>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          <h3 className="font-heading text-xl font-bold text-brand-text line-clamp-2 group-hover:text-primary transition-colors mb-3">
            {guide.title}
          </h3>
          
          {guide.excerpt && (
            <p className="text-brand-textSecondary text-sm line-clamp-2 mb-4">
              {guide.excerpt}
            </p>
          )}

          {/* Meta */}
          <div className="flex flex-wrap gap-4 text-sm text-brand-textMuted">
            {guide.duration && (
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {guide.duration}
              </span>
            )}
            {guide.estimated_cost && (
              <span className="flex items-center gap-1">
                <DollarSign className="h-4 w-4" />
                {guide.estimated_cost}
              </span>
            )}
            {guide.municipio && (
              <span className="flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                {guide.municipio}
              </span>
            )}
          </div>

          {/* Checklist preview */}
          {guide.checklist && guide.checklist.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-xs text-brand-textMuted uppercase tracking-wider mb-2">
                Qué llevar:
              </p>
              <div className="flex flex-wrap gap-2">
                {guide.checklist.slice(0, 3).map((item, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1 text-xs bg-brand-bg px-2 py-1 rounded-full"
                  >
                    <Check className="h-3 w-3 text-primary" />
                    {item}
                  </span>
                ))}
                {guide.checklist.length > 3 && (
                  <span className="text-xs text-brand-textMuted">
                    +{guide.checklist.length - 3} más
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </Link>
    </motion.div>
  );
}

export default function GuiasPage() {
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [difficulty, setDifficulty] = useState("all");
  const [location, setLocation] = useState("all");

  useEffect(() => {
    fetchGuides();
  }, [difficulty, location]);

  const fetchGuides = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (difficulty !== "all") params.append("difficulty", difficulty);
      if (location !== "all") params.append("location_scope", location);
      params.append("limit", "20");
      
      const response = await axios.get(`${API}/public/guides?${params}`);
      setGuides(response.data.items);
    } catch (error) {
      console.error("Error fetching guides:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      {/* Hero */}
      <PageHero
        section="guias"
        title="Guías y Experiencias"
        subtitle="Itinerarios, rutas y consejos prácticos para explorar Choluteca y Honduras como un local."
        badge="Explora"
        badgeIcon={Compass}
      />

      {/* Filters */}
      <section className="bg-white border-b border-gray-100 sticky top-16 md:top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 text-brand-textSecondary">
              <Compass className="h-5 w-5" />
              Filtrar por:
            </div>
            <Select value={difficulty} onValueChange={setDifficulty}>
              <SelectTrigger className="w-40 rounded-full" data-testid="difficulty-filter">
                <SelectValue placeholder="Dificultad" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="Fácil">Fácil</SelectItem>
                <SelectItem value="Moderado">Moderado</SelectItem>
                <SelectItem value="Difícil">Difícil</SelectItem>
              </SelectContent>
            </Select>
            <Select value={location} onValueChange={setLocation}>
              <SelectTrigger className="w-40 rounded-full" data-testid="location-filter">
                <SelectValue placeholder="Ubicación" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="choluteca">Choluteca</SelectItem>
                <SelectItem value="honduras">Honduras</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="py-12 md:py-16" data-testid="guias-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-gray-100 rounded-3xl h-[400px] animate-pulse" />
              ))}
            </div>
          ) : guides.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {guides.map((guide) => (
                <GuideCard key={guide.guide_id} guide={guide} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-3xl">
              <Compass className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-600 mb-2">
                Próximamente más guías
              </h3>
              <p className="text-gray-500">
                Estamos preparando guías increíbles para ti.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Ad Banner */}
      <section className="py-8 bg-gray-50">
        <AdBanner />
      </section>
    </Layout>
  );
}
